import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { listProjects } from "../lib/api";
import { useI18n } from "../context/I18nContext";
import { Building2, FolderKanban, ListTree, AlertCircle, ChevronRight, Plus } from "lucide-react";

const EMPTY_STATE_BG = "https://static.prod-images.emergentagent.com/jobs/de8b7e50-5120-4fa2-90f1-4b615beda34b/images/61d863dd4a195e64efffbcd1f9ce72f7e92c72e4d59c1ed69163263ed4d49058.png";

function StatCard({ icon: Icon, label, value, testid, accent }) {
  return (
    <div className="bg-white border border-zinc-200 rounded-md p-5 hover:border-zinc-300 transition-colors" data-testid={testid}>
      <div className="flex items-start justify-between">
        <div>
          <div className="cs-label">{label}</div>
          <div className="font-mono text-3xl font-semibold text-zinc-950 mt-2">{value}</div>
        </div>
        <Icon size={20} className={accent || "text-zinc-400"} />
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { t } = useI18n();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const data = await listProjects();
        setProjects(data.projects || []);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const totals = projects.reduce(
    (acc, p) => {
      acc.syncs += p.stats?.syncs || 0;
      acc.tasks += p.stats?.tasks || 0;
      acc.restrictions += p.stats?.open_restrictions || 0;
      return acc;
    },
    { syncs: 0, tasks: 0, restrictions: 0 }
  );

  return (
    <div className="px-8 lg:px-12 py-10 max-w-7xl" data-testid="dashboard-page">
      <header className="flex items-end justify-between">
        <div>
          <div className="cs-label">overview</div>
          <h1 className="font-heading text-4xl font-bold tracking-tight text-zinc-950 mt-2" data-testid="dashboard-title">{t("dashboard_title")}</h1>
          <p className="text-sm text-zinc-600 mt-1">{t("dashboard_subtitle")}</p>
        </div>
        <Link to="/projects" className="inline-flex items-center gap-2 bg-cs-accent text-white px-4 py-2.5 rounded-md text-sm font-semibold hover:bg-cs-accentHover transition-colors" data-testid="dashboard-new-project-btn">
          <Plus size={16} /> {t("new_project")}
        </Link>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-8">
        <StatCard icon={Building2} label={t("total_projects")} value={projects.length} testid="stat-projects" />
        <StatCard icon={FolderKanban} label={t("total_syncs")} value={totals.syncs} testid="stat-syncs" />
        <StatCard icon={ListTree} label={t("total_tasks")} value={totals.tasks} testid="stat-tasks" />
        <StatCard icon={AlertCircle} label={t("open_restrictions")} value={totals.restrictions} testid="stat-restrictions" accent="text-cs-critical" />
      </div>

      <section className="mt-12">
        <div className="flex items-center justify-between">
          <h2 className="font-heading text-xl font-semibold text-zinc-900">{t("recent_projects")}</h2>
        </div>

        {loading ? (
          <div className="mt-6 text-sm text-zinc-500" data-testid="dashboard-loading">{t("loading")}</div>
        ) : projects.length === 0 ? (
          <div className="mt-6 border border-zinc-200 rounded-md overflow-hidden bg-white" data-testid="dashboard-empty">
            <div className="grid-blueprint h-44" style={{ backgroundImage: `url('${EMPTY_STATE_BG}'), linear-gradient(rgba(0,0,0,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(0,0,0,0.04) 1px, transparent 1px)`, backgroundSize: "cover, 32px 32px, 32px 32px", backgroundPosition: "center" }} />
            <div className="p-6">
              <h3 className="font-heading text-lg font-semibold text-zinc-900">{t("no_projects")}</h3>
              <Link to="/projects" className="inline-flex items-center gap-2 mt-3 text-sm font-semibold text-cs-accent hover:underline" data-testid="dashboard-empty-cta">
                {t("new_project")} <ChevronRight size={14} />
              </Link>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
            {projects.slice(0, 9).map((p) => (
              <Link to={`/projects/${p.id}`} key={p.id} className="bg-white border border-zinc-200 rounded-md p-5 hover:border-zinc-300 hover:shadow-sm transition-all group" data-testid={`project-card-${p.id}`}>
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="inline-block w-2 h-2 rounded-full" style={{ background: p.color || "#F97316" }} />
                      <h3 className="font-heading font-semibold text-zinc-950 truncate">{p.name}</h3>
                    </div>
                    {p.client_name && <div className="text-xs text-zinc-500 mt-1">{p.client_name}</div>}
                  </div>
                  <ChevronRight size={16} className="text-zinc-400 group-hover:text-cs-accent transition-colors" />
                </div>
                <div className="grid grid-cols-3 gap-3 mt-5 pt-4 border-t border-zinc-100">
                  <div>
                    <div className="cs-label !text-[10px]">{t("syncs")}</div>
                    <div className="font-mono text-sm font-semibold text-zinc-900">{p.stats?.syncs || 0}</div>
                  </div>
                  <div>
                    <div className="cs-label !text-[10px]">{t("tasks")}</div>
                    <div className="font-mono text-sm font-semibold text-zinc-900">{p.stats?.tasks || 0}</div>
                  </div>
                  <div>
                    <div className="cs-label !text-[10px]">{t("restrictions")}</div>
                    <div className="font-mono text-sm font-semibold text-zinc-900">{p.stats?.open_restrictions || 0}</div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
